/**
 * Left offsets for a run of frozen table columns, MEASURED rather than declared.
 *
 * Both OPA tables first pinned their columns with hard-coded offsets, on the reasoning that sticky
 * needs a real number and measuring would reflow. That reasoning was wrong in a way that only showed
 * up as a bug (Max, 2026-08-30): a `width` on a `<td>` in an auto-layout table is a HINT. The browser
 * grows a column past it whenever the content asks — "OPA-000061" plus its sort caret took a column
 * declared at 108px out to 138 — and then every offset after it is short by the difference, leaving a
 * strip of the scrolling table showing between two columns that are supposed to be flush.
 *
 * So the offsets come from the header cells' real widths. The measurement runs in a layout effect,
 * before paint, and again on resize; `left` does not affect width, so setting it cannot feed back
 * into what was measured.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export default function useFrozenColumns(count, deps = []) {
  const ref = useRef(null);
  const [lefts, setLefts] = useState(() => new Array(count).fill(0));

  const measure = useCallback(() => {
    const head = ref.current && ref.current.querySelector('thead tr');
    if (!head) return;
    const cells = Array.from(head.children).slice(0, count);
    if (cells.length < count) return;
    let acc = 0;
    const next = cells.map((cell) => {
      const at = acc;
      acc += cell.getBoundingClientRect().width;
      return Math.round(at);
    });
    setLefts((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next));
  }, [count]);

  useLayoutEffect(measure, [measure, ...deps]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  return [ref, lefts];
}
